import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatDialog} from "@angular/material/dialog";
import {UserService} from "../../../../@shared/services/user.service";
import {PaybackDataSource} from "./paybacks.data-source";
import {IPayback} from "../../../../@core/enums/payback/interfaces/payback.interface";
import {PaybackDialogComponent} from "./payback-dialog/payback-dialog.component";
import {filter} from "rxjs/operators";
import {IUserWallet} from "../services/interfaces/user-wallet.interface";
import {IPaybackWallet} from "../../../../@core/enums/payback/interfaces/payback-wallet/payback-wallet.interface";
import {WalletTypeEnum} from "../../../../@core/enums/wallet-type.enum";
import {WalletTypeIdEnum} from "../../../../@core/enums/wallet-type-id.enum";
import {
  IPaybackWalletDialogData,
  PaybackWalletDialogComponent
} from "./payback-wallet-dialog/payback-wallet-dialog.component";

interface IPaybackWalletRow {
  walletId: number;
  walletType: WalletTypeEnum;
  payback: number;
  paybackWallet?: IPaybackWallet;
}

@Component({
  selector: 'app-paybacks',
  templateUrl: './paybacks.component.html',
  styleUrls: ['./paybacks.component.scss']
})
export class PaybacksComponent implements AfterViewInit {
  @ViewChild(MatPaginator, {static: false}) paginator!: MatPaginator;

  public paybackColumns: string[] = ['userId', 'payback', 'type', 'actionType', 'lockType', 'createdAt', 'actions'];
  public paybackSource = new PaybackDataSource(this.userService);

  public paybackWalletColumns: string[] = ['walletType', 'payback', 'actions'];
  public paybackWalletRows: IPaybackWalletRow[] = [];
  public paybackWalletsLoading: boolean = false;

  constructor(private userService: UserService,
              private dialog: MatDialog) {
    this.loadPaybacks();
    this.loadPaybackWallets();
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe(() => this.loadPaybacks());
  }

  loadPaybacks(): void {
    this.paybackSource.loadData();
  }

  loadPaybackWallets(): void {
    this.paybackWalletsLoading = true;

    this.userService.getUserWallets().subscribe((wallets: IUserWallet[]) => {
      if (!wallets?.length) {
        this.paybackWalletRows = [];
        this.paybackWalletsLoading = false;
        return;
      }

      const walletIds: number[] = wallets.map(w => w.id);

      this.userService.getPaybackWallets({
        walletIds,
        pagination: {limit: walletIds.length, offset: 0},
      }).subscribe(({paybackWallets}) => {
        this.paybackWalletRows = paybackWallets
          .map((pw): IPaybackWalletRow | null => {
            const wallet = wallets.find(w => w.id === pw.walletId);
            const walletType = wallet ? this.mapWalletTypeIdToEnum(wallet.walletType?.id) : null;
            if (!walletType) return null;

            return {
              walletId: pw.walletId,
              walletType,
              payback: pw.payback,
              paybackWallet: pw,
            };
          })
          .filter((row): row is IPaybackWalletRow => row !== null);

        this.paybackWalletsLoading = false;
      });
    });
  }

  createPayback(): void {
    const params = {
      width: 'fit-content',
      height: 'fit-content',
    }
    this.dialog.open(PaybackDialogComponent, params).afterClosed()
      .pipe(filter(v => !!v))
      .subscribe(() => this.loadPaybacks());
  }

  updatePayback(item: IPayback): void {
    const params = {
      width: 'fit-content',
      height: 'fit-content',
      data: item
    }
    this.dialog.open(PaybackDialogComponent, params).afterClosed()
      .pipe(filter(v => !!v))
      .subscribe(() => this.loadPaybacks());
  }

  updatePaybackWallet(row: IPaybackWalletRow): void {
    const data: IPaybackWalletDialogData = {
      walletType: row.walletType,
      paybackWallet: row.paybackWallet,
    };

    this.dialog.open(PaybackWalletDialogComponent, {
      width: 'fit-content',
      height: 'fit-content',
      data,
    }).afterClosed()
      .pipe(filter(v => !!v))
      .subscribe(() => this.loadPaybackWallets());
  }

  private mapWalletTypeIdToEnum(walletTypeId: number | undefined): WalletTypeEnum | null {
    switch (walletTypeId) {
      case WalletTypeIdEnum.REAL: return WalletTypeEnum.REAL;
      case WalletTypeIdEnum.DEMO: return WalletTypeEnum.DEMO;
      case WalletTypeIdEnum.BONUS: return WalletTypeEnum.BONUS;
      case WalletTypeIdEnum.BONUS_PIECES: return WalletTypeEnum.BONUS_PIECES;
      case WalletTypeIdEnum.FREE_COINS: return WalletTypeEnum.FREE_COINS;
      case WalletTypeIdEnum.QUEST_WALLET_1: return WalletTypeEnum.QUEST_WALLET_1;
      case WalletTypeIdEnum.QUEST_WALLET_2: return WalletTypeEnum.QUEST_WALLET_2;
      case WalletTypeIdEnum.EVENT_WALLET_1: return WalletTypeEnum.EVENT_WALLET_1;
      default: return null;
    }
  }
}
